import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import styles from './HomeServices.module.css';
import { useLang } from '../context/LanguageContext';

/* ─── Icons ─────────────────────────────────────────────── */
const IconGlobe = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);
const IconCode = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
  </svg>
);
const IconChip = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="7" y="7" width="10" height="10" rx="1"/>
    <path d="M7 9H4M7 12H4M7 15H4M17 9h3M17 12h3M17 15h3M9 7V4M12 7V4M15 7V4M9 17v3M12 17v3M15 17v3"/>
  </svg>
);
const IconWrench = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
  </svg>
);

/* ─── Data ───────────────────────────────────────────────── */
const CARDS = [
  { id: 'web',   Icon: IconGlobe,  featured: false },
  { id: 'dev',   Icon: IconCode,   featured: false },
  { id: 'ai',    Icon: IconChip,   featured: true  },
  { id: 'maint', Icon: IconWrench, featured: false },
];

/* ─── Variants ───────────────────────────────────────────── */
const wv = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};
const cv = {
  hidden:  { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] } },
};

/* ─── Component ──────────────────────────────────────────── */
const HomeServices = () => {
  const ref = useRef(null);
  const inV = useInView(ref, { once: true, margin: '-80px' });
  const { t } = useLang();
  const [titleLine1, titleLine2] = t.homeServices.title.split('\n');

  return (
    <section className={styles.section} ref={ref}>
      <div className={styles.topLine} />
      <div className={styles.container}>

        {/* Header */}
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: 20 }}
          animate={inV ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className={styles.eyebrow}>{t.homeServices.eyebrow}</span>
          <h2 className={styles.title}>{titleLine1}<br />{titleLine2}</h2>
        </motion.div>

        {/* Grid */}
        <motion.div
          className={styles.grid}
          variants={wv}
          initial="hidden"
          animate={inV ? 'visible' : 'hidden'}
        >
          {CARDS.map(({ id, Icon, featured }, idx) => (
            <motion.div
              key={id}
              className={featured ? `${styles.card} ${styles.cardFeatured}` : styles.card}
              variants={cv}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
            >
              <div className={styles.iconWrap}>
                <Icon />
              </div>
              <h3 className={styles.cardTitle}>{t.homeServices.cards[idx].title}</h3>
              <p className={styles.cardDesc}>{t.homeServices.cards[idx].desc}</p>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </section>
  );
};

export default HomeServices;
